import {
  ALL_DOMAINS,
  APPLICATION_DOMAINS,
  MasteryThreshold,
  ResourceBudget,
  ZERO_TRUST_DOMAINS,
} from "./config";
import { AppError, ErrorClassification } from "./errors";
import {
  AnswerOutcome,
  ExamMode,
  MasteryState,
  Track,
  type Domain as DomainType,
  type DomainScore,
  type Question,
  type QuestionResult,
  type ResponseMap,
  type ScoreReport,
} from "./types";

const RANDOM_MULTIPLIER = 16_807;
const RANDOM_MODULUS = 2_147_483_647;
const RANDOM_SEED_FLOOR = 1;

export function masteryForPercentage(percentage: number): MasteryState {
  if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) {
    throw new AppError(
      "Mastery percentage must be between zero and one hundred.",
      ErrorClassification.INVARIANT_VIOLATION,
      "INVALID_MASTERY_PERCENTAGE",
    );
  }

  if (percentage >= MasteryThreshold.READY_MINIMUM) {
    return MasteryState.READY;
  }

  if (percentage >= MasteryThreshold.PROFICIENT_MINIMUM) {
    return MasteryState.PROFICIENT;
  }

  if (percentage >= MasteryThreshold.DEVELOPING_MINIMUM) {
    return MasteryState.DEVELOPING;
  }

  return MasteryState.FOUNDATIONAL;
}

export function scoreAttempt(
  questions: readonly Question[],
  responses: ResponseMap,
): ScoreReport {
  if (questions.length === 0) {
    throw new AppError(
      "An attempt must contain at least one question.",
      ErrorClassification.INVARIANT_VIOLATION,
      "EMPTY_ATTEMPT",
    );
  }

  if (questions.length > ResourceBudget.MAX_QUESTION_COUNT) {
    throw new AppError(
      "The attempt exceeds the configured question budget.",
      ErrorClassification.PERMANENT,
      "QUESTION_BUDGET_EXCEEDED",
    );
  }

  const questionIds = new Set(questions.map((question) => question.id));
  if (questionIds.size !== questions.length) {
    throw new AppError(
      "Question identifiers must be unique within an attempt.",
      ErrorClassification.INVARIANT_VIOLATION,
      "DUPLICATE_QUESTION_ID",
    );
  }

  for (const responseId of Object.keys(responses)) {
    if (!questionIds.has(responseId)) {
      throw new AppError(
        "A response referenced a question outside the current attempt.",
        ErrorClassification.PERMANENT,
        "UNKNOWN_RESPONSE_QUESTION",
      );
    }
  }

  const results = questions.map((question): QuestionResult => {
    const selectedChoiceId = responses[question.id] ?? null;
    const validChoiceIds = new Set(question.choices.map((choice) => choice.id));

    if (selectedChoiceId !== null && !validChoiceIds.has(selectedChoiceId)) {
      throw new AppError(
        "A response referenced an invalid choice.",
        ErrorClassification.PERMANENT,
        "INVALID_RESPONSE_CHOICE",
      );
    }

    const outcome =
      selectedChoiceId === null
        ? AnswerOutcome.UNANSWERED
        : selectedChoiceId === question.correctChoiceId
          ? AnswerOutcome.CORRECT
          : AnswerOutcome.INCORRECT;

    return {
      questionId: question.id,
      track: question.track,
      domain: question.domain,
      selectedChoiceId,
      correctChoiceId: question.correctChoiceId,
      outcome,
    };
  });

  const correct = results.filter(
    (result) => result.outcome === AnswerOutcome.CORRECT,
  ).length;
  const percentage = percentageOf(correct, questions.length);
  const domainScores = scoreDomains(results);
  const weakestDomainPercentage = Math.min(
    ...domainScores.map((score) => score.percentage),
  );
  const mastery =
    percentage >= MasteryThreshold.READY_MINIMUM
      ? weakestDomainPercentage >= MasteryThreshold.READY_DOMAIN_MINIMUM
        ? MasteryState.READY
        : MasteryState.PROFICIENT
      : masteryForPercentage(percentage);

  return {
    correct,
    total: questions.length,
    percentage,
    mastery,
    domainScores,
    results,
  };
}

function scoreDomains(results: readonly QuestionResult[]): readonly DomainScore[] {
  const presentDomains = ALL_DOMAINS.filter((domain) =>
    results.some((result) => result.domain === domain),
  );

  return presentDomains.map((domain) => {
    const domainResults = results.filter((result) => result.domain === domain);
    const correct = domainResults.filter(
      (result) => result.outcome === AnswerOutcome.CORRECT,
    ).length;
    const percentage = percentageOf(correct, domainResults.length);

    return {
      domain,
      correct,
      total: domainResults.length,
      percentage,
      mastery: masteryForPercentage(percentage),
    };
  });
}

function percentageOf(numerator: number, denominator: number): number {
  if (denominator <= 0) {
    throw new AppError(
      "A percentage denominator must be positive.",
      ErrorClassification.INVARIANT_VIOLATION,
      "INVALID_PERCENTAGE_DENOMINATOR",
    );
  }

  return Math.round((numerator / denominator) * 100);
}

export function selectQuestions(
  questions: readonly Question[],
  domains: readonly DomainType[],
  requestedCount: number,
  seed: number,
): readonly Question[] {
  if (
    domains.length === 0 ||
    requestedCount <= 0 ||
    requestedCount > ResourceBudget.MAX_QUESTION_COUNT
  ) {
    throw new AppError(
      "Question selection parameters are outside the configured budget.",
      ErrorClassification.PERMANENT,
      "INVALID_SELECTION_PARAMETERS",
    );
  }

  const uniqueDomains = new Set(domains);
  if (uniqueDomains.size !== domains.length) {
    throw new AppError(
      "Question selection domains must be unique.",
      ErrorClassification.INVARIANT_VIOLATION,
      "DUPLICATE_SELECTION_DOMAIN",
    );
  }

  const eligibleQuestions = questions.filter((question) =>
    uniqueDomains.has(question.domain),
  );

  if (eligibleQuestions.length < requestedCount) {
    throw new AppError(
      "The loaded question chunks cannot satisfy the requested assessment.",
      ErrorClassification.INVARIANT_VIOLATION,
      "INSUFFICIENT_QUESTIONS",
    );
  }

  const random = createSeededRandom(seed);
  const domainQueues = domains.map((domain) =>
    shuffled(
      eligibleQuestions.filter((question) => question.domain === domain),
      random,
    ),
  );

  if (domainQueues.some((queue) => queue.length === 0)) {
    throw new AppError(
      "Every selected domain must have at least one loaded question.",
      ErrorClassification.INVARIANT_VIOLATION,
      "EMPTY_DOMAIN_CHUNK",
    );
  }

  const selected: Question[] = [];
  let queueIndex = 0;

  while (selected.length < requestedCount) {
    const queue = domainQueues[queueIndex];
    if (queue === undefined) {
      throw new AppError(
        "Question selection queue index is invalid.",
        ErrorClassification.INVARIANT_VIOLATION,
        "INVALID_QUEUE_INDEX",
      );
    }

    const nextQuestion = queue.shift();
    if (nextQuestion !== undefined) {
      selected.push(nextQuestion);
    }

    queueIndex = (queueIndex + 1) % domainQueues.length;
  }

  return shuffled(selected, random);
}

export function domainsForMode(
  mode: ExamMode,
  focusDomains: readonly DomainType[],
): readonly DomainType[] {
  switch (mode) {
    case ExamMode.COMPREHENSIVE:
      return ALL_DOMAINS;
    case ExamMode.APPLICATION_SECURITY:
      return APPLICATION_DOMAINS;
    case ExamMode.ZERO_TRUST:
      return ZERO_TRUST_DOMAINS;
    case ExamMode.FOCUS:
      if (focusDomains.length === 0) {
        throw new AppError(
          "Focus practice requires assessed weak domains.",
          ErrorClassification.PERMANENT,
          "FOCUS_NOT_AVAILABLE",
        );
      }
      return focusDomains;
  }
}

export function questionCountForMode(mode: ExamMode): number {
  switch (mode) {
    case ExamMode.COMPREHENSIVE:
      return ResourceBudget.COMPREHENSIVE_QUESTION_COUNT;
    case ExamMode.APPLICATION_SECURITY:
    case ExamMode.ZERO_TRUST:
      return ResourceBudget.TRACK_QUESTION_COUNT;
    case ExamMode.FOCUS:
      return ResourceBudget.FOCUS_QUESTION_COUNT;
  }
}

export function trackForDomain(domain: DomainType): Track {
  return ZERO_TRUST_DOMAINS.includes(
    domain as (typeof ZERO_TRUST_DOMAINS)[number],
  )
    ? Track.ZERO_TRUST
    : Track.APPLICATION_SECURITY;
}

function createSeededRandom(seed: number): () => number {
  if (!Number.isFinite(seed)) {
    throw new AppError(
      "Randomization seed must be finite.",
      ErrorClassification.PERMANENT,
      "INVALID_RANDOM_SEED",
    );
  }

  let state = Math.abs(Math.trunc(seed)) % RANDOM_MODULUS;
  if (state < RANDOM_SEED_FLOOR) {
    state = RANDOM_SEED_FLOOR;
  }

  return () => {
    state = (state * RANDOM_MULTIPLIER) % RANDOM_MODULUS;
    return (state - RANDOM_SEED_FLOOR) / (RANDOM_MODULUS - RANDOM_SEED_FLOOR);
  };
}

function shuffled<T>(values: readonly T[], random: () => number): T[] {
  const copy = [...values];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    const currentValue = copy[index];
    const swapValue = copy[swapIndex];

    if (currentValue === undefined || swapValue === undefined) {
      throw new AppError(
        "Shuffle indices must reference existing values.",
        ErrorClassification.INVARIANT_VIOLATION,
        "INVALID_SHUFFLE_INDEX",
      );
    }

    copy[index] = swapValue;
    copy[swapIndex] = currentValue;
  }

  return copy;
}
